import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContentNugget, ImageCard, PageHeader, toolTransform } from '@backrs_ui/common-ui';

import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { NuggetCardList } from '@backrs_ui/grow-ui/src/components/NuggetCards/NuggetCardList';
import { handlePanelNuggetClick } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { useNuggetRedirect } from '@backrs_ui/grow-ui/src/hooks/useNuggetRedirect';

import { Box, SxProps, Theme } from '@mui/material';

import { useGetToolsPageSections } from './hooks/useGetToolsPageSections';
import { useAgentContext } from './ToolsPage.agentContext';

export const ToolsPage = () => {
  const navigate = useNavigate();
  const { collegeSolutions, guides, exploratoryTools, careerSolutions, backrsAssessments } =
    useGetToolsPageSections();

  useNuggetRedirect();
  useAgentContext({
    collegeSolutions,
    guides,
    exploratoryTools,
    careerSolutions,
    backrsAssessments,
  });

  const collegeNuggets = useMemo(
    () =>
      collegeSolutions.actionStickerCards
        .filter((c): c is typeof c & { tool: NonNullable<typeof c.tool> } => !!c.tool)
        .map((c) => toolTransform(c.tool)),
    [collegeSolutions.actionStickerCards],
  );
  const careerNuggets = useMemo(
    () =>
      careerSolutions.actionStickerCards
        .filter((c): c is typeof c & { tool: NonNullable<typeof c.tool> } => !!c.tool)
        .map((c) => toolTransform(c.tool)),
    [careerSolutions.actionStickerCards],
  );
  const assessmentNuggets = useMemo(
    () =>
      backrsAssessments.actionStickerCards
        .filter((c): c is typeof c & { tool: NonNullable<typeof c.tool> } => !!c.tool)
        .map((c) => toolTransform(c.tool)),
    [backrsAssessments.actionStickerCards],
  );

  const handleNuggetClick = (nugget: ContentNugget) => {
    handlePanelNuggetClick(nugget, navigate);
  };

  return (
    <Box sx={containerSx}>
      <Box sx={contentContainerSx}>
        <PageHeader
          title={collegeSolutions.title}
          subtitle={collegeSolutions.description}
          maxWidth={null}
          rightComponents={[
            <LubavButton
              key="tools-college-lubav-btn"
              id="tools-college-lubav-btn"
              label="Which tool should I start with?"
              promptButtonId="tools_college_start"
            />,
          ]}
        />
        <Box sx={nuggetListMaxWidthSx}>
          <NuggetCardList
            nuggets={collegeNuggets}
            onClick={handleNuggetClick}
            direction="scrollingRow"
            hideActions
          />
        </Box>

        <PageHeader
          title={guides.title}
          subtitle={guides.description}
          maxWidth={null}
          rightComponents={[
            <LubavButton
              key="tools-guides-lubav-btn"
              id="tools-guides-lubav-btn"
              label="Which guide should I read?"
              prompt={`Based ONLY on the guides listed in the ${guides.title} section of this page, which guide should I read? Do not recommend tools from other sections.`}
              allowTools={false}
            />,
          ]}
        />
        <Box sx={nuggetListMaxWidthSx}>
          <NuggetCardList
            nuggets={guides.nuggets}
            onClick={handleNuggetClick}
            direction="scrollingRow"
            hideActions
          />
        </Box>

        <PageHeader
          title={exploratoryTools.title}
          subtitle={exploratoryTools.description}
          maxWidth={null}
          rightComponents={[
            <LubavButton
              key="tools-search-lubav-btn"
              id="tools-search-lubav-btn"
              label="What should I look for?"
              prompt={`Based ONLY on the tools listed in the ${exploratoryTools.title} section of this page, which search tool should I use? Do not recommend tools from other sections.`}
              allowTools={false}
            />,
          ]}
        />
        <Box sx={imageCardScrollContainerSx}>
          {exploratoryTools.imageCards.map((imageCard) => (
            <ImageCard
              key={imageCard.title}
              title={imageCard.title}
              description={imageCard.description}
              imageUrl={imageCard.imageUrl}
              onClick={imageCard.onClick}
              sxOverrides={{ container: imageCardOverrideSx }}
            />
          ))}
        </Box>

        <PageHeader
          title={careerSolutions.title}
          subtitle={careerSolutions.description}
          maxWidth={null}
          rightComponents={[
            <LubavButton
              key="tools-career-lubav-btn"
              id="tools-career-lubav-btn"
              label="Which tool is best for me?"
              prompt="Based ONLY on the tools listed in the Career Solutions section of this page, which tool is best for me? Do not recommend tools from other sections."
              allowTools={false}
            />,
          ]}
        />
        <Box sx={nuggetListMaxWidthSx}>
          <NuggetCardList
            nuggets={careerNuggets}
            onClick={handleNuggetClick}
            direction="scrollingRow"
            hideActions
          />
        </Box>

        <PageHeader
          title={backrsAssessments.title}
          subtitle={backrsAssessments.description}
          maxWidth={null}
        />
        <Box sx={nuggetListMaxWidthSx}>
          <NuggetCardList
            nuggets={assessmentNuggets}
            onClick={handleNuggetClick}
            direction="scrollingRow"
            hideActions
          />
        </Box>
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  alignSelf: 'center',
  gap: backrs.spacing['spacing-2xl'],
});

const nuggetListMaxWidthSx: SxProps<Theme> = {};

const imageCardScrollContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  overflowX: 'auto',
  gap: backrs.spacing['spacing-4xl'],
  alignItems: 'stretch',
  padding: backrs.spacing['spacing-md'],
  margin: `calc(-1 * ${backrs.spacing['spacing-md']})`,
});

const imageCardOverrideSx: SxProps<Theme> = {
  flex: 'none',
  minWidth: '288px',
  maxWidth: '288px',
};
